import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import { isTaskOverdue } from 'app/lib/taskDueDate'
import { debug } from 'lib/logging'

export interface TaskCounts {
  completed: number
  open: number
  overdue: number
}
export type TaskCountsMap = Record<string, TaskCounts>

interface TaskHit {
  contactId?: string | null
  status?: string
  dueDate?: string
}

export const PIPELINE_TASK_COUNTS_QUERY = graphql(`
  query PipelineTaskCounts($search: String, $pagination: PaginationInput) {
    tasks(search: $search, pagination: $pagination) {
      hits {
        contactId
        status
        dueDate
      }
    }
  }
`)

export function groupTaskCounts(hits: TaskHit[], now: Date): TaskCountsMap {
  const map: TaskCountsMap = {}
  for (const t of hits) {
    if (!t.contactId) continue
    const c = (map[t.contactId] ??= { completed: 0, open: 0, overdue: 0 })
    if (t.status === `closed`) c.completed += 1
    else if (t.status === `open`) {
      // Calendar-day compare: a task due today is open, not overdue.
      if (isTaskOverdue(t, now)) c.overdue += 1
      else c.open += 1
    }
  }
  return map
}

/**
 * Fetch task counts for every contact on the board in ONE query.
 * `enabled` is false when no slot needs task counts, so we fetch nothing.
 */
export function usePipelineTaskCounts(contactIds: string[], enabled: boolean): TaskCountsMap {
  const ids = enabled ? contactIds.filter(Boolean) : []
  const search = ids.length ? `contactId:(${ids.join(` OR `)})` : undefined
  const [result] = useQuery({
    query: PIPELINE_TASK_COUNTS_QUERY,
    variables: { search, pagination: { limit: 1000 } },
    pause: ids.length === 0,
  })
  const hits: TaskHit[] = result.data?.tasks?.hits ?? []
  if (hits.length >= 1000) {
    debug([`pipelines`], `task counts hit the 1000-task page limit; some contacts may be undercounted`)
  }
  return groupTaskCounts(hits, new Date())
}
